import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, KeyRound, Loader2, SlidersHorizontal, TriangleAlert, Users } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import {
  ASSISTANT_MODELS, connectionStatus, dollarsToCents, formFromSettings, formsEqual, integerOrNull, settingsFromForm, withinLimits,
} from "../lib/assistant";
import { getInitials, avatarColorClass } from "../lib/avatar";
import { roleBadgeClass } from "../lib/roles";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Paramètres › Copilote Jamora — administrator only (route and every API call: require_admin, `assistant:manage`).
export default function SettingsAssistant() {
  const [data, setData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = async () => {
    setLoadFailed(false);
    try {
      const { data: body } = await api.get("/assistant/settings");
      setData(body);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []);

  if (loadFailed) return <LoadError onRetry={load} />;
  if (data == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-assistant">
      <SectionHeader
        title="Copilote Jamora"
        description="Assistant IA intégré à Jamora. Il ne voit et ne fait jamais plus que ce que la personne connectée a le droit de voir et de faire."
      />
      <ConnectionCard connection={data.connection} onChange={(connection) => setData((d) => ({ ...d, connection }))} />
      <LimitsCard settings={data.settings} onSaved={(settings) => setData((d) => ({ ...d, settings }))} />
      <AccessCard />
    </div>
  );
}

function CardTitle({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-jamora/10 text-jamora flex items-center justify-center flex-shrink-0">
        <Icon size={16} />
      </div>
      <div>
        <div className="font-display font-semibold text-sm text-slate-900">{title}</div>
        {description && <p className="text-xs text-slate-500 mt-0.5 max-w-xl">{description}</p>}
      </div>
    </div>
  );
}

const TONE_DOT = { ok: "bg-emerald-500", warning: "bg-amber-500", muted: "bg-slate-300" };

function ConnectionCard({ connection, onChange }) {
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const status = connectionStatus(connection);

  const save = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.put("/assistant/credentials", { api_key: apiKey });
      setApiKey("");
      setTestResult(null);
      onChange(data);
      toast.success("Clé API enregistrée");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/assistant/credentials/test");
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, message: errorMessage(err) });
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      const { data } = await api.delete("/assistant/credentials");
      onChange(data);
      setTestResult(null);
      setConfirmRemove(false);
      toast.success("Clé API supprimée");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="assistant-connection">
      <CardTitle icon={KeyRound} title="Connexion à Anthropic"
        description="La clé API est chiffrée avant d'être enregistrée et n'est jamais réaffichée. Créez-la dans la console Anthropic." />

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <div className="text-sm">
          <div className="flex items-center gap-2 font-medium text-slate-900" data-testid="assistant-key-status">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TONE_DOT[status.tone]}`} />
            {status.label}
          </div>
          {connection.configured && connection.updated_at && (
            <div className="text-xs text-slate-500 mt-0.5 ml-3.5">
              Modifiée {connection.updated_by_name ? `par ${connection.updated_by_name} ` : ""}le{" "}
              {new Date(connection.updated_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>
        {connection.configured && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={test} disabled={testing || !connection.readable} data-testid="btn-assistant-test">
              {testing && <Loader2 size={14} className="mr-1.5 animate-spin" />}Tester la connexion
            </Button>
            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setConfirmRemove(true)}
              data-testid="btn-assistant-remove-key">
              Supprimer la clé
            </Button>
          </div>
        )}
      </div>

      {testResult && (
        <div role="status" data-testid="assistant-test-result"
          className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${testResult.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`}>
          {testResult.ok ? <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> : <TriangleAlert size={16} className="mt-0.5 flex-shrink-0" />}
          <span>{testResult.message}</span>
        </div>
      )}

      <form onSubmit={save} className="space-y-1.5 max-w-xl">
        <Label htmlFor="assistant-api-key" className="text-sm">{connection.configured ? "Remplacer la clé" : "Clé API Anthropic"}</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input id="assistant-api-key" type="password" autoComplete="off" spellCheck={false} placeholder="sk-ant-…"
            value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid="input-assistant-api-key" />
          <Button type="submit" disabled={saving || !apiKey.trim()} data-testid="btn-assistant-save-key">
            {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer la clé
          </Button>
        </div>
      </form>

      <AlertDialog open={confirmRemove} onOpenChange={(open) => !removing && setConfirmRemove(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la clé API ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le Copilote ne pourra plus répondre à personne tant qu'une nouvelle clé n'aura pas été enregistrée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); remove(); }} disabled={removing}
              className="bg-red-600 hover:bg-red-700" data-testid="btn-assistant-remove-confirm">
              {removing && <Loader2 size={14} className="mr-1.5 animate-spin" />}Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NumberField({ id, label, hint, suffix, value, onChange, valid, inputMode = "numeric" }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm">{label}</Label>
      <div className="flex items-center gap-2 max-w-[200px]">
        <Input id={id} inputMode={inputMode} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!valid}
          data-testid={`input-${id}`} />
        {suffix && <span className="text-sm text-slate-500 whitespace-nowrap">{suffix}</span>}
      </div>
      <p className={`text-xs ${valid ? "text-slate-400" : "text-red-600"}`}>{hint}</p>
    </div>
  );
}

function LimitsCard({ settings, onSaved }) {
  const saved = useMemo(() => formFromSettings(settings), [settings]);
  const [form, setForm] = useState(saved);
  const [advanced, setAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(saved), [saved]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const payload = settingsFromForm(form);
  const dirty = !formsEqual(form, saved);
  const model = ASSISTANT_MODELS.find((m) => m.value === form.model);

  const save = async () => {
    if (!payload) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/assistant/settings", payload);
      onSaved(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5" data-testid="assistant-limits">
        <CardTitle icon={SlidersHorizontal} title="Modèle et plafonds"
          description="Les montants sont en dollars, la devise de facturation d'Anthropic. Les plafonds s'appliqueront dès l'ouverture des conversations." />

        <div className="border-t border-slate-100 pt-4">
          <div className="space-y-1.5 max-w-md">
            <Label className="text-sm">Modèle</Label>
            <Select value={form.model} onValueChange={set("model")}>
              <SelectTrigger data-testid="select-assistant-model"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSISTANT_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {model && <p className="text-xs text-slate-500">{model.hint}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <NumberField id="assistant-monthly-budget" label="Plafond mensuel (équipe)" suffix="$ / mois" inputMode="decimal"
            value={form.monthly_budget} onChange={set("monthly_budget")} valid={withinLimits("monthly_budget_cents", dollarsToCents(form.monthly_budget))}
            hint="0 = sans plafond." />
          <NumberField id="assistant-daily-budget" label="Plafond par personne" suffix="$ / jour" inputMode="decimal"
            value={form.daily_user_budget} onChange={set("daily_user_budget")} valid={withinLimits("daily_user_budget_cents", dollarsToCents(form.daily_user_budget))}
            hint="0 = sans plafond." />
          <NumberField id="assistant-retention" label="Conservation des échanges" suffix="jours"
            value={form.retention_days} onChange={set("retention_days")} valid={withinLimits("retention_days", integerOrNull(form.retention_days))}
            hint="Entre 7 et 365 jours. Les chiffres de consommation sont gardés 24 mois, sans texte." />
        </div>

        <div className="border-t border-slate-100 pt-3">
          <button type="button" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="btn-assistant-advanced">
            <ChevronDown size={14} className={`transition ${advanced ? "rotate-180" : ""}`} /> Réglages avancés
          </button>
          {advanced && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-4">
              <NumberField id="assistant-tool-rounds" label="Étapes par question"
                value={form.max_tool_rounds} onChange={set("max_tool_rounds")} valid={withinLimits("max_tool_rounds", integerOrNull(form.max_tool_rounds))}
                hint="Consultations successives de Jamora pour une même question (1 à 12)." />
              <NumberField id="assistant-output-tokens" label="Longueur maximale d'une réponse" suffix="tokens"
                value={form.max_output_tokens} onChange={set("max_output_tokens")} valid={withinLimits("max_output_tokens", integerOrNull(form.max_output_tokens))}
                hint="Entre 256 et 8 000." />
            </div>
          )}
        </div>
      </div>
      <SaveBar dirty={dirty} saving={saving} canManage saveDisabled={!payload} onSave={save} onRevert={() => setForm(saved)} />
    </>
  );
}

function AccessCard() {
  const [items, setItems] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState({});

  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/assistant/access");
      setItems(data.items);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []);

  // Saved at once, like the notification preferences: optimistic, put back if the server refuses.
  const toggle = async (person, enabled) => {
    setPending((p) => ({ ...p, [person.id]: true }));
    setItems((list) => list.map((u) => (u.id === person.id ? { ...u, enabled } : u)));
    try {
      await api.patch(`/assistant/access/${person.id}`, { enabled });
      toast.success(enabled ? `${person.name} peut utiliser le Copilote` : `Accès au Copilote coupé pour ${person.name}`);
    } catch (e) {
      setItems((list) => list.map((u) => (u.id === person.id ? { ...u, enabled: !enabled } : u)));
      toast.error(errorMessage(e));
    } finally {
      setPending((p) => ({ ...p, [person.id]: false }));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="assistant-access">
      <CardTitle icon={Users} title="Qui peut utiliser le Copilote"
        description="Les comptes actifs dont le rôle porte « Utiliser le Copilote Jamora ». Coupez l'accès d'une personne sans changer son rôle." />

      <div className="border-t border-slate-100 pt-2">
        {loadFailed && (
          <div className="py-6 text-center space-y-2">
            <p className="text-sm text-slate-500">Impossible de charger la liste des accès.</p>
            <Button variant="outline" size="sm" onClick={load}>Réessayer</Button>
          </div>
        )}
        {!loadFailed && items == null && (
          <div className="space-y-2 pt-2">{[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-lg bg-slate-100 animate-pulse" />)}</div>
        )}
        {items?.length === 0 && (
          <div className="py-6 text-center space-y-3" data-testid="assistant-access-empty">
            <p className="text-sm text-slate-700 font-medium">Personne n'a encore accès au Copilote</p>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Ajoutez la permission « Utiliser le Copilote Jamora » au rôle des personnes concernées.
            </p>
            <Button asChild variant="outline" size="sm"><Link to="/roles">Rôles & permissions</Link></Button>
          </div>
        )}
        {items?.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {items.map((person) => (
              <li key={person.id} className="flex items-center gap-3 py-2.5" data-testid={`assistant-access-${person.id}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColorClass()}`}>
                  {getInitials(person.name, person.email)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{person.name}</div>
                  <div className="text-xs text-slate-500 truncate">{person.email}</div>
                </div>
                <span className={`hidden sm:inline-flex text-xs font-medium rounded-full px-2 py-0.5 ${roleBadgeClass(person.role)}`}>{person.role_label}</span>
                <Switch checked={person.enabled} disabled={!!pending[person.id]} onCheckedChange={(value) => toggle(person, value)}
                  aria-label={`Autoriser ${person.name} à utiliser le Copilote`} data-testid={`switch-assistant-access-${person.id}`} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
